import React, { useState, useEffect, useCallback } from 'react';
import { auth } from '../firebase';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useModal } from './ModalContext';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { X, Wallet, CreditCard, Shield, Sparkles } from 'lucide-react';

// --- Configuration Constants ---
const API_BASE_URL = 'https://ufb-1.onrender.com/api/wallet';
const RAZORPAY_KEY_ID = 'rzp_live_NU6JCcb56EISZl'; // IMPORTANT: Move to an .env file for production
const MIN_RECHARGE_AMOUNT = 0;
const MAX_RECHARGE_AMOUNT = 500000;
const QUICK_RECHARGE_AMOUNTS = [500, 1000, 2500, 5000, 10000];

/**
 * A professional and smooth wallet recharge component with a minimalist black and white UI.
 */
const WalletComponent = () => {
    const [amount, setAmount] = useState('');
    const [balance, setBalance] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFetchingBalance, setIsFetchingBalance] = useState(true);
    const { setIsModalOpen } = useModal();
    const user = auth.currentUser;

    const handleClose = useCallback(() => {
        setIsModalOpen(false);
    }, [setIsModalOpen]);

    const fetchWalletBalance = useCallback(async () => {
        if (!user) return;

        setIsFetchingBalance(true);
        try {
            const token = await user.getIdToken();
            console.log(token)
            const response = await axios.get(`${API_BASE_URL}/balance/${user.uid}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            setBalance(response.data.balance || 0);
        } catch (error) {
            console.error('Error fetching wallet balance:', error.message);
            toast.error('Failed to fetch wallet balance.');
            setBalance(0);
        } finally {
            setIsFetchingBalance(false);
        }
    }, [user]);

    useEffect(() => {
        if (user) {
            fetchWalletBalance();
        } else {
            toast.error('Please log in to use the wallet.');
            handleClose();
        }
    }, [user, fetchWalletBalance, handleClose]);

    const handleAmountChange = (value) => {
        const numericValue = parseInt(value.replace(/,/g, ''), 10);
        if (value === '' || isNaN(numericValue)) {
            setAmount('');
            return;
        }

        if (numericValue > MAX_RECHARGE_AMOUNT) {
            toast.warn(`Maximum amount is ₹${MAX_RECHARGE_AMOUNT.toLocaleString()}`);
            setAmount(MAX_RECHARGE_AMOUNT.toString());
        } else {
            setAmount(numericValue.toString());
        }
    };

    const handleWalletPayment = useCallback(async () => {
        const rechargeAmount = parseFloat(amount);
        if (rechargeAmount < MIN_RECHARGE_AMOUNT) {
            toast.error(`Minimum recharge amount is ₹${MIN_RECHARGE_AMOUNT}.`);
            return;
        }

        setIsSubmitting(true);
        try {
            const token = await user.getIdToken();
            const orderResponse = await axios.post(`${API_BASE_URL}/create-recharge-order`,
                { amount: rechargeAmount, userId: user.uid },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            const { order } = orderResponse.data;
            if (!order) throw new Error("Failed to create payment order.");

            const options = {
                key: RAZORPAY_KEY_ID,
                amount: order.amount,
                currency: 'INR',
                name: 'Wallet Recharge',
                description: 'Add money to your secure wallet',
                order_id: order.id,
                handler: async (response) => {
                    try {
                        const verifyResponse = await axios.post(`${API_BASE_URL}/verify-payment`,
                            { ...response, userId: user.uid },
                            { headers: { 'Authorization': `Bearer ${token}` } }
                        );

                        if (verifyResponse.data.success) {
                            setBalance(verifyResponse.data.newBalance);
                            toast.success('Recharge successful! Your wallet is updated.');
                            handleClose();
                        } else {
                            throw new Error("Payment verification failed.");
                        }
                    } catch (error) {
                        console.error('Error verifying payment:', error.message);
                        toast.error('Payment failed. Please contact support.');
                    } finally {
                        setIsSubmitting(false);
                    }
                },
                theme: { color: '#000000' },
                modal: {
                    ondismiss: () => {
                        setIsSubmitting(false);
                        toast.info('Payment was cancelled.');
                    },
                },
            };

            const rzp = new window.Razorpay(options);
            rzp.open();
        } catch (error) {
            console.error('Payment initialization error:', error.message);
            toast.error('Could not initiate payment. Please try again.');
            setIsSubmitting(false);
        }
    }, [amount, user, handleClose]);

    const isPaymentDisabled = parseFloat(amount) < MIN_RECHARGE_AMOUNT || isSubmitting;
    const currentAmount = parseFloat(amount) || 0;

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-6">
            <ToastContainer position="top-right" theme="dark" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
            <div className="bg-white text-black rounded-xl shadow-2xl w-full max-w-lg md:max-w-2xl transform transition-all duration-300">
                <header className="relative border-b border-gray-200 p-6">
                    <button onClick={handleClose} className="absolute top-4 right-4 p-2 text-gray-500 bg-white hover:bg-gray-100 rounded-full transition-colors" aria-label="Close modal">
                        <X size={22} />
                    </button>
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-gray-100 border border-gray-200 rounded-lg">
                            <Wallet size={24} className="text-black" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold">Recharge Wallet</h2>
                            <p className="text-gray-500 text-sm">Add money to your account securely</p>
                        </div>
                    </div>
                </header>

                <main className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="md:col-span-1 space-y-6">
                        <section className="space-y-3">
                            <label htmlFor="amount" className="block text-sm font-semibold text-gray-800">Enter Recharge Amount</label>
                            <div className="relative">
                                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 font-semibold text-xl">₹</span>
                                <input
                                    type="text"
                                    id="amount"
                                    value={amount ? parseInt(amount).toLocaleString('en-IN') : ''}
                                    onChange={(e) => handleAmountChange(e.target.value)}
                                    placeholder="0"
                                    className="w-full pl-10 pr-4 py-4 border-2 border-gray-200 bg-gray-50 rounded-lg focus:border-black focus:ring-0 text-2xl font-bold transition-colors"
                                />
                            </div>
                        </section>

                        <section className="space-y-3">
                            <p className="text-sm font-semibold text-gray-800">Quick Select</p>
                            <div className="grid grid-cols-3 gap-2">
                                {QUICK_RECHARGE_AMOUNTS.map((value) => (
                                    <button
                                        key={value}
                                        onClick={() => handleAmountChange(value.toString())}
                                        className={`py-2 px-3 rounded-md text-sm font-bold transition-all duration-200 border-2 ${
                                            currentAmount === value
                                                ? 'bg-black text-white border-black scale-105 shadow-lg'
                                                : 'bg-white text-black border-gray-200 hover:bg-gray-100 hover:border-black'
                                        }`}
                                    >
                                        ₹{value.toLocaleString()}
                                    </button>
                                ))}
                            </div>
                        </section>

                        <div className="flex items-center gap-3 text-xs text-gray-500 bg-gray-100 p-3 rounded-md">
                            <Shield size={20} className="text-black flex-shrink-0" />
                            <span>Your payment is secured with 256-bit SSL encryption.</span>
                        </div>
                    </div>

                    <div className="md:col-span-1 bg-gray-50/70 rounded-lg p-6 space-y-6 border border-gray-200 flex flex-col">
                        <section className="flex-grow space-y-6">
                            <h3 className="text-lg font-bold text-black">Summary</h3>
                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-500 font-medium">Current Balance</span>
                                    {isFetchingBalance ? (
                                        <div className="animate-pulse bg-gray-200 h-7 w-24 rounded-md"></div>
                                    ) : (
                                        <span className="text-2xl font-bold text-black">
                                            ₹{Math.round(balance).toLocaleString()}
                                        </span>
                                    )}
                                </div>
                            </div>
                            {currentAmount >= MIN_RECHARGE_AMOUNT && (
                                <section className="space-y-3">
                                    <div className="space-y-2">
                                        <div className="flex justify-between text-sm"><span className="text-gray-500">Recharge Amount</span><span className="font-semibold">₹{currentAmount.toLocaleString()}</span></div>
                                        <div className="flex justify-between text-sm"><span className="text-gray-500">Credited to Wallet</span><span className="font-semibold">₹{currentAmount.toLocaleString()}</span></div>
                                        <hr className="border-gray-200 my-2" />
                                        <div className="flex justify-between text-lg font-bold"><span>Total Payable</span><span>₹{currentAmount.toLocaleString()}</span></div>
                                    </div>
                                </section>
                            )}
                        </section>

                        <footer className="space-y-3">
                            <button
                                onClick={handleWalletPayment}
                                disabled={isPaymentDisabled}
                                className={`w-full py-4 rounded-lg font-bold text-base transition-all duration-300 flex items-center justify-center gap-2 ${
                                    isPaymentDisabled
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-black text-white hover:bg-gray-800 shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
                                }`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <CreditCard size={20} />
                                        Continue to Pay ₹{currentAmount.toLocaleString()}
                                    </>
                                )}
                            </button>
                            <p className="text-xs text-center text-gray-500">
                                By continuing, you agree to our{' '}
                                <Link to="/terms-and-conditions" className="text-black hover:underline font-semibold">
                                    Terms and Conditions
                                </Link>.
                            </p>
                        </footer>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default WalletComponent;